import { CreatePopUpRequest, GetEmailCaptureResponse, GetPopUpResponse, PopUpAnalytics } from '../../types';
import { buildQueryString, del, emptyPost, get, logWithAmplitude, post, put, throwHttpError } from './api';

import { TELEMETRY_ENDPOINT } from '../constants';

/**
 * Get all popups for an organization (admin view)
 */
export async function getOrganizationPopups(
	amplitude_callsite: string,
	organization_id: string,
	organization_key?: string,
): Promise<GetPopUpResponse[]> {
	const qs = buildQueryString({ organization_key });
	const response = await fetch(`/api/popups/${organization_id}${qs}`, get());
	if (!response.ok) {
		await throwHttpError('Failed to fetch organization popups', response);
	}

	const popups: GetPopUpResponse[] = await response.json();
	logWithAmplitude(amplitude_callsite, 'Get Organization Popups', { organization_id });
	return popups;
}

/**
 * Get popups for a specific topic (public endpoint, used by map viewer)
 */
export async function getTopicPopups(topic_id: string): Promise<GetPopUpResponse[]> {
	const response = await fetch(`/api/popups/public/${topic_id}`, get(false));
	if (!response.ok) {
		await throwHttpError('Failed to fetch topic popups', response);
	}

	const popups: GetPopUpResponse[] = await response.json();
	return popups;
}

/**
 * Create a new popup
 */
export async function createPopup(
	amplitude_callsite: string,
	organization_id: string,
	popup: CreatePopUpRequest,
	organization_key?: string,
): Promise<GetPopUpResponse> {
	const qs = buildQueryString({ organization_key });
	const response = await fetch(`/api/popups/${organization_id}${qs}`, post(popup));
	if (!response.ok) {
		await throwHttpError('Failed to create popup', response);
	}

	const created: GetPopUpResponse = await response.json();
	logWithAmplitude(amplitude_callsite, 'Create Popup', {
		popup_id: created._id.$oid,
		organization_id,
		popup_type: popup.pop_up_type,
	});
	return created;
}

/**
 * Update an existing popup
 */
export async function updatePopup(
	amplitude_callsite: string,
	organization_id: string,
	popup_id: string,
	popup: CreatePopUpRequest,
	organization_key?: string,
): Promise<GetPopUpResponse> {
	const qs = buildQueryString({ popup_id, organization_key });
	const response = await fetch(`/api/popups/${organization_id}${qs}`, put(popup));
	if (!response.ok) {
		await throwHttpError('Failed to update popup', response);
	}

	console.log('Popup update response status:', response.status);

	const updated: GetPopUpResponse = await response.json();
	logWithAmplitude(amplitude_callsite, 'Update Popup', {
		popup_id,
		organization_id,
		popup_type: popup.pop_up_type,
	});
	return updated;
}

/**
 * Delete a popup
 */
export async function deletePopup(
	amplitude_callsite: string,
	organization_id: string,
	popup_id: string,
	organization_key?: string,
): Promise<void> {
	const qs = buildQueryString({ popup_id, organization_key });
	const response = await fetch(`/api/popups/${organization_id}${qs}`, del());
	if (!response.ok) {
		await throwHttpError('Failed to delete popup', response);
	}

	logWithAmplitude(amplitude_callsite, 'Delete Popup', {
		popup_id,
		organization_id,
	});
}

/**
 * Track popup events (impression, click, dismiss)
 * Fire-and-forget endpoint - failures are silently ignored
 */
export async function trackPopupEvent(
	popup_id: string,
	topic_id: string,
	event: 'impression' | 'click' | 'dismiss',
): Promise<void> {
	const qs = buildQueryString({ popup_id, topic_id, event });
	try {
		await fetch(`${TELEMETRY_ENDPOINT}/api/popups/track${qs}`, emptyPost());
	} catch {
		// Fire-and-forget: silently ignore tracking failures
	}
}

/**
 * Get legacy email captures (no popup_id, no point_id) for an organization
 */
export async function getLegacyEmailCaptures(
	amplitude_callsite: string,
	organization_id: string,
	organization_key?: string,
): Promise<GetEmailCaptureResponse[]> {
	const qs = buildQueryString({ organization_key });
	const response = await fetch(`/api/popups/${organization_id}/legacy_email_captures${qs}`, get());
	if (!response.ok) {
		await throwHttpError('Failed to fetch legacy email captures', response);
	}

	const captures: GetEmailCaptureResponse[] = await response.json();
	logWithAmplitude(amplitude_callsite, 'Get Legacy Email Captures', { organization_id });
	return captures;
}

/**
 * Get email captures for a specific popup
 */
export async function getPopupEmailCaptures(
	amplitude_callsite: string,
	organization_id: string,
	popup_id: string,
	organization_key?: string,
): Promise<GetEmailCaptureResponse[]> {
	const qs = buildQueryString({ organization_key });
	const response = await fetch(`/api/popups/${organization_id}/email_captures/${popup_id}${qs}`, get());
	if (!response.ok) {
		await throwHttpError('Failed to fetch popup email captures', response);
	}

	const captures: GetEmailCaptureResponse[] = await response.json();
	logWithAmplitude(amplitude_callsite, 'Get Popup Email Captures', { organization_id, popup_id });
	return captures;
}

/**
 * Get analytics for all popups in an organization
 */
export async function getPopupAnalytics(
	amplitude_callsite: string,
	organization_id: string,
	topic_id?: string,
	organization_key?: string,
): Promise<PopUpAnalytics[]> {
	const qs = buildQueryString({ organization_key, topic_id });
	const response = await fetch(`/api/popups/${organization_id}/analytics${qs}`, get());
	if (!response.ok) {
		await throwHttpError('Failed to fetch popup analytics', response);
	}

	const analytics: PopUpAnalytics[] = await response.json();
	logWithAmplitude(amplitude_callsite, 'Get Popup Analytics', { organization_id });
	return analytics;
}
